// El cartel de un grupo de mesas («mesa de equipo», web/qa/auditoria-apple/propuesta-2/fuentes/grupos.md §3-4): la banda
// de arriba de su placa, en la capa DOM. Nombre y cuántos («Precios 3»); si está atado a un contrato con Santi y la pelota
// la tiene Santi, 🤝 y el nombre en cian, y a la derecha el contrato (abre su hilo). Al pasar el ratón (o con el foco),
// «+» (contratar en este grupo) y «⋯» (su menú). Sin color propio: el color es para «actúa» y para los estados.
//   · Clic en el nombre o F2: renombrar en el sitio (Intro guarda, Esc cancela; un nombre repetido se avisa aquí).
//   · Supr: deshacer el grupo (6 s para volver atrás). Ctrl+←/→: moverlo antes o después en la sala.
//   · En «Ordenar» se arrastra el cartel y la isla entera va con él.
// «Sin grupo» lleva solo su etiqueta (`EtiquetaSinGrupo`): ni placa ni menú.
import * as Menu from '@radix-ui/react-dropdown-menu';
import { Check, ChevronRight, FileText, Handshake, Moon, MoreHorizontal, Pencil, Plus, Sun, Ungroup, type LucideIcon } from 'lucide-react';
import { memo, useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent as EventoTeclado, type PointerEvent as EventoPuntero, type ReactNode } from 'react';
import { t } from '../../../i18n';
import { cn } from '../../../lib/utils';
import { LIMITES } from '../compartido/vocabulario';
import type { EsperaDeGrupo } from '../pelotas/porAgente';
import type { ContratoDTO } from '../pelotas/tipos';
import { estiloDe } from './geometria';
import type { GrupoMundo } from './generar';

/** Lo que el cartel pide a la escena. Identidad fija (useAccionesEstables): no rompe el memo. */
export interface AccionesCartel {
  contratarAqui: (g: GrupoMundo) => void;
  empezarRenombrar: (grupoId: string) => void;
  /** null = cancelar. Devuelve el error a enseñar en el cartel (nombre repetido), o null si se guardó. */
  terminarRenombrar: (grupoId: string, nombre: string | null) => Promise<string | null>;
  aparcarLos: (grupoId: string, aparcar: boolean) => void;
  atar: (grupoId: string, temaId: number | null) => void;
  deshacer: (grupoId: string) => void;
  /** Ctrl+←/→: antes (−1) o después (+1) en la sala. */
  moverOrden: (grupoId: string, paso: 1 | -1) => void;
  abrirContrato: (temaId: number) => void;
  /** En «Ordenar»: pulsar en el cartel empieza a arrastrar el grupo entero. */
  agarrarCartel: (e: EventoPuntero, g: GrupoMundo) => void;
}

export interface PropsCartel {
  g: GrupoMundo & { grupoId: string };
  z: number;
  espera: EsperaDeGrupo | undefined;
  /** El dueño del contrato atado ya no está en la oficina (caso 2: el grupo sigue esperando). */
  duenoSeFue: boolean;
  /** Los contratos abiertos con Santi (para atar uno); null si la oficina no está conectada a su tablero (caso 6). */
  contratos: readonly ContratoDTO[] | null;
  /** Cuántos de sus agentes se pueden aparcar (quietos y despiertos) y cuántos duermen. */
  aparcables: number;
  dormidos: number;
  ordenando: boolean;
  renombrando: boolean;
  /**
   * 04-11 (#269) — el nombre que propone la recepción para el grupo recién creado, cuando llega. Sustituye a la sugerencia
   * local en el campo SOLO si Pau aún no ha tecleado nada (nunca pisa lo suyo).
   */
  propuesta?: string;
  /** Va a recibir lo que se suelta (un agente o, en «Ordenar», otro grupo). */
  destino: boolean;
  acciones: AccionesCartel;
}

const BOTON_CARTEL =
  'inline-flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-oficina-texto2 outline-none transition-colors duration-150 ease-o-apple hover:bg-oficina-control hover:text-oficina-texto focus-visible:ring-2 focus-visible:ring-oficina-enlace data-[state=open]:bg-oficina-control data-[state=open]:text-oficina-texto';

export const CartelGrupo = memo(function CartelGrupo({ g, z, espera, duenoSeFue, contratos, aparcables, dormidos, ordenando, renombrando, propuesta, destino, acciones }: PropsCartel) {
  const conSanti = !!espera?.conSanti;
  const [menu, setMenu] = useState(false);
  const raiz = useRef<HTMLDivElement>(null);
  const teclas = (e: EventoTeclado<HTMLDivElement>) => {
    if (renombrando || e.target !== e.currentTarget) return;
    if (e.key === 'F2') {
      e.preventDefault();
      acciones.empezarRenombrar(g.grupoId);
    } else if (e.key === 'Delete') {
      e.preventDefault();
      acciones.deshacer(g.grupoId);
    } else if ((e.ctrlKey || e.metaKey) && (e.key === 'ArrowLeft' || e.key === 'ArrowRight')) {
      e.preventDefault();
      acciones.moverOrden(g.grupoId, e.key === 'ArrowRight' ? 1 : -1);
    }
  };
  return (
    <>
      {/* La placa se ilumina cuando se va a soltar aquí (meter a un agente o, ordenando, colocar otro grupo). */}
      {destino ? <div className="pointer-events-none absolute rounded-o-s ring-2 ring-oficina-texto2" style={estiloDe(g.placa, z)} data-testid="placa-destino" aria-hidden /> : null}
      <div
        ref={raiz}
        role="group"
        tabIndex={0}
        aria-label={[t('oficinaV2.grupos.cartelAria', { nombre: g.nombre, n: g.n }), conSanti ? t('oficinaV2.grupos.esperandoSanti') : null].filter(Boolean).join(' · ')}
        onKeyDown={teclas}
        onPointerDown={(e) => ordenando && e.target === e.currentTarget && acciones.agarrarCartel(e, g)}
        className={cn(
          'group/cartel absolute flex items-center gap-1.5 rounded-o-s outline-none focus-visible:ring-2 focus-visible:ring-oficina-enlace',
          ordenando && 'cursor-grab touch-none',
        )}
        style={{ ...estiloDe(g.cartel, z), paddingLeft: 3 * z, paddingRight: 2 * z }}
        data-testid="cartel-grupo"
        data-grupo={g.grupoId}
      >
        {conSanti ? <Handshake className="h-4 w-4 shrink-0 text-oficina-santi" strokeWidth={2.2} aria-hidden /> : null}
        {renombrando ? (
          <CampoNombre inicial={g.nombre} propuesta={propuesta} alTerminar={(n) => acciones.terminarRenombrar(g.grupoId, n)} />
        ) : (
          <button
            type="button"
            onClick={() => acciones.empezarRenombrar(g.grupoId)}
            title={t('oficinaV2.grupos.renombrarPista')}
            className={cn(
              // El nombre no cede sitio (hasta el ancho del cartel): lo que se recorta es el contrato de la derecha.
              'max-w-full shrink-0 truncate rounded-o-s px-1 text-left text-o-texto font-semibold outline-none hover:bg-oficina-control focus-visible:ring-2 focus-visible:ring-oficina-enlace',
              conSanti ? 'text-oficina-santi' : 'text-oficina-texto',
            )}
            data-testid="cartel-nombre"
          >
            {g.nombre}
          </button>
        )}
        {renombrando ? null : <span className="shrink-0 text-o-sub tabular-nums text-oficina-texto3" data-testid="cartel-n">{g.n}</span>}
        <span className="min-w-0 flex-1" />
        {espera && !renombrando ? (
          <button
            type="button"
            onClick={() => acciones.abrirContrato(espera.contrato.temaId)}
            title={duenoSeFue ? t('oficinaV2.grupos.duenoSeFue') : t('oficinaV2.grupos.abrirContrato', { titulo: espera.contrato.titulo })}
            className="flex min-w-0 items-center gap-1 rounded-o-s px-1 text-o-nota text-oficina-texto3 outline-none hover:bg-oficina-control hover:text-oficina-texto2 focus-visible:ring-2 focus-visible:ring-oficina-enlace"
            data-testid="cartel-contrato"
          >
            <FileText className="h-3.5 w-3.5 shrink-0" aria-hidden />
            <span className="truncate">{duenoSeFue ? t('oficinaV2.grupos.duenoSeFueCorto') : espera.contrato.titulo}</span>
          </button>
        ) : null}
        {renombrando || ordenando ? null : (
          // «+» y «⋯»: al pasar el ratón o con el foco dentro (con el dedo, siempre); abiertos, se quedan. Escondidos no
          // ocupan sitio: en reposo el contrato de la derecha se lee entero.
          <span
            className={cn(
              'flex shrink-0 items-center gap-0.5',
              !menu && '[@media(hover:hover)_and_(pointer:fine)]:hidden [@media(hover:hover)_and_(pointer:fine)]:group-hover/cartel:flex [@media(hover:hover)_and_(pointer:fine)]:group-focus-within/cartel:flex',
            )}
          >
            <button type="button" onClick={() => acciones.contratarAqui(g)} aria-label={t('oficinaV2.grupos.contratarAqui', { nombre: g.nombre })} title={t('oficinaV2.grupos.contratarAqui', { nombre: g.nombre })} className={BOTON_CARTEL} data-testid="cartel-contratar">
              <Plus className="h-4 w-4" aria-hidden />
            </button>
            <Menu.Root open={menu} onOpenChange={setMenu}>
              <Menu.Trigger asChild>
                <button type="button" aria-label={t('oficinaV2.grupos.menu', { nombre: g.nombre })} className={BOTON_CARTEL} data-testid="cartel-menu">
                  <MoreHorizontal className="h-4 w-4" aria-hidden />
                </button>
              </Menu.Trigger>
              <Menu.Portal>
                <Menu.Content
                  align="end"
                  sideOffset={6}
                  collisionPadding={8}
                  onCloseAutoFocus={(e) => {
                    e.preventDefault();
                    raiz.current?.focus({ preventScroll: true });
                  }}
                  className="z-[70] min-w-60 rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95"
                >
                  <Opcion icono={Pencil} atajo="F2" alElegir={() => acciones.empezarRenombrar(g.grupoId)}>
                    {t('oficinaV2.grupos.renombrar')}
                  </Opcion>
                  {contratos ? (
                    <Menu.Sub>
                      <Menu.SubTrigger className={OPCION}>
                        <Handshake className="h-4 w-4 text-oficina-texto2" aria-hidden />
                        <span className="flex-1">{t('oficinaV2.grupos.esperarContrato')}</span>
                        <ChevronRight className="h-4 w-4 shrink-0 text-oficina-texto3" aria-hidden />
                      </Menu.SubTrigger>
                      <Menu.Portal>
                        <Menu.SubContent sideOffset={4} collisionPadding={8} className="z-[71] max-h-80 min-w-64 max-w-sm overflow-y-auto rounded-o-m bg-oficina-elevada p-1 shadow-o-hoja ring-1 ring-white/10 animate-in fade-in-0 zoom-in-95">
                          {contratos.length ? (
                            contratos.map((c) => (
                              <Opcion key={c.temaId} icono={espera?.contrato.temaId === c.temaId ? Check : null} alElegir={() => acciones.atar(g.grupoId, c.temaId)}>
                                {c.titulo}
                              </Opcion>
                            ))
                          ) : (
                            <p className="px-3 py-2 text-o-sub text-oficina-texto3">{t('oficinaV2.grupos.sinContratos')}</p>
                          )}
                          {espera ? (
                            <>
                              <Menu.Separator className="my-1 h-px bg-oficina-linea" />
                              <Opcion icono={null} alElegir={() => acciones.atar(g.grupoId, null)}>
                                {t('oficinaV2.grupos.noEsperar')}
                              </Opcion>
                            </>
                          ) : null}
                        </Menu.SubContent>
                      </Menu.Portal>
                    </Menu.Sub>
                  ) : null}
                  {aparcables ? (
                    <Opcion icono={Moon} alElegir={() => acciones.aparcarLos(g.grupoId, true)}>
                      {t('oficinaV2.grupos.aparcarLos', { n: aparcables })}
                    </Opcion>
                  ) : null}
                  {dormidos ? (
                    <Opcion icono={Sun} alElegir={() => acciones.aparcarLos(g.grupoId, false)}>
                      {t('oficinaV2.grupos.despertarLos', { n: dormidos })}
                    </Opcion>
                  ) : null}
                  <Menu.Separator className="my-1 h-px bg-oficina-linea" />
                  <Opcion icono={Ungroup} atajo={t('oficinaV2.grupos.atajoSupr')} alElegir={() => acciones.deshacer(g.grupoId)}>
                    {t('oficinaV2.grupos.deshacer')}
                  </Opcion>
                </Menu.Content>
              </Menu.Portal>
            </Menu.Root>
          </span>
        )}
      </div>
    </>
  );
});

/** «Sin grupo 2»: la etiqueta del último bloque de una sala con grupos (sin placa ni menú). */
export const EtiquetaSinGrupo = memo(function EtiquetaSinGrupo({ g, z, destino }: { g: GrupoMundo; z: number; destino: boolean }) {
  return (
    <>
      {destino ? <div className="pointer-events-none absolute rounded-o-s ring-2 ring-oficina-texto2" style={estiloDe(g.placa, z)} aria-hidden /> : null}
      <p className="pointer-events-none absolute flex items-center gap-1.5 text-o-sub" style={{ ...estiloDe(g.cartel, z), paddingLeft: 4 * z }} data-testid="sin-grupo">
        <span className="font-medium text-oficina-texto2">{t('oficinaV2.grupos.sinGrupo')}</span>
        {g.n ? <span className="tabular-nums text-oficina-texto3">{g.n}</span> : null}
      </p>
    </>
  );
});

const OPCION = 'flex min-h-11 cursor-pointer items-center gap-2.5 rounded-o-s px-3 py-2 text-o-cuerpo text-oficina-texto outline-none data-[highlighted]:bg-oficina-control data-[state=open]:bg-oficina-control';

function Opcion({ icono: Icono, atajo, alElegir, children }: { icono: LucideIcon | null; atajo?: string; alElegir: () => void; children: ReactNode }) {
  return (
    <Menu.Item onSelect={alElegir} className={OPCION}>
      {Icono ? <Icono className="h-4 w-4 shrink-0 text-oficina-texto2" aria-hidden /> : <span className="h-4 w-4 shrink-0" aria-hidden />}
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {atajo ? <kbd className="shrink-0 font-sans text-o-nota text-oficina-texto3 [@media(pointer:coarse)]:hidden">{atajo}</kbd> : null}
    </Menu.Item>
  );
}

/**
 * El nombre editable en el cartel: Intro guarda, Esc cancela, salir del campo guarda. Un error se queda a la vista. Si
 * llega la `propuesta` de la recepción y Pau no ha tecleado, la pone (seleccionada: escribir la sustituye); si ya tecleó,
 * se ignora.
 */
function CampoNombre({ inicial, propuesta, alTerminar }: { inicial: string; propuesta?: string; alTerminar: (nombre: string | null) => Promise<string | null> }) {
  const [valor, setValor] = useState(inicial);
  const [error, setError] = useState<string | null>(null);
  const campo = useRef<HTMLInputElement>(null);
  const enviando = useRef(false);
  const tocado = useRef(false);
  const seleccionar = useRef(false);
  useEffect(() => {
    campo.current?.focus();
    campo.current?.select();
  }, []);
  useEffect(() => {
    if (!propuesta || tocado.current || enviando.current) return;
    seleccionar.current = true;
    setValor(propuesta.slice(0, LIMITES.grupo));
  }, [propuesta]);
  useLayoutEffect(() => {
    if (!seleccionar.current) return;
    seleccionar.current = false;
    if (document.activeElement === campo.current) campo.current?.select();
  }, [valor]);
  const terminar = async (nombre: string | null) => {
    if (enviando.current) return;
    enviando.current = true;
    const e = await alTerminar(nombre === null ? null : nombre.trim());
    enviando.current = false;
    setError(e);
    if (e) campo.current?.focus();
  };
  return (
    <span className="relative min-w-0 flex-1">
      <input
        ref={campo}
        value={valor}
        maxLength={LIMITES.grupo}
        onChange={(e) => {
          tocado.current = true;
          setValor(e.target.value);
          setError(null);
        }}
        onKeyDown={(e) => {
          e.stopPropagation();
          if (e.key === 'Enter') {
            e.preventDefault();
            void terminar(valor);
          } else if (e.key === 'Escape') {
            e.preventDefault();
            void terminar(null);
          }
        }}
        onBlur={() => void terminar(valor)}
        aria-label={t('oficinaV2.grupos.nombreAria')}
        aria-invalid={error ? true : undefined}
        className="h-6 w-full rounded-o-s bg-oficina-control px-1.5 text-o-texto font-semibold text-oficina-texto outline-none ring-2 ring-oficina-enlace"
        data-testid="cartel-campo"
      />
      {error ? (
        <span role="alert" className="absolute left-0 top-full z-10 mt-1 whitespace-nowrap rounded-o-s bg-oficina-elevada px-2 py-1 text-o-nota font-medium text-oficina-error shadow-o-flota">
          {error}
        </span>
      ) : null}
    </span>
  );
}
